import { RemoteConfigProperties } from "../../../../..";
import { NotImplementedError } from "../../../../../errors";
import { PromiseHandler } from "../../../../../handlers";
import { parseDateFilter, parseReportTypeFilter, requireAdvertiserIdFilter, requireAnalysisInterval } from "../../../../../models/looker/modules/attribution";
import { toEmbedHash } from "../../../../../models/looker/query";
import { FILTER_OPTIONS_PIXEL_QUERY } from "../query";
export default class LookerAttributionPixelFilter extends PromiseHandler {
    constructor(sdk) {
        super(sdk, "looker-attribution-pixel-filter");
    }
    async findItems(filters) {
        const { reportTypes, lookerMediaTypeFilter } = parseReportTypeFilter(filters.where);
        // cross-channel/filter-options only accepts VIDEO/AUDIO/DISPLAY as
        // report_type — pixels are a digital concept there, with no linear
        // equivalent on that endpoint. This is a live path for Custom Reporting's
        // linear reports (mapCustomReportTypeToAttributionReportType maps
        // CustomReportType.LINEAR to "LINEAR" before calling this handler), so a
        // LINEAR selection stays on the Looker path regardless of the flag,
        // rather than earning a 400 the caller never used to see.
        if (this.sdk.featureFlags.isFlagEnabled(RemoteConfigProperties.REPORTING_API_ATTRIBUTION_FILTERS) &&
            !reportTypes.includes("LINEAR")) {
            return this.findItemsFromReportingApi(filters);
        }
        const dateFilter = filters.where?.find((filter) => filter.field === "dates");
        if (!dateFilter) {
            throw new Error("Dates filter is required");
        }
        const parsedDateFilter = parseDateFilter(dateFilter);
        const advertiserIdFilter = filters.where?.find((filter) => filter.field === "advertiser")?.value;
        if (!advertiserIdFilter) {
            throw new Error("Advertiser ID filter is required");
        }
        const queryBody = {
            ...FILTER_OPTIONS_PIXEL_QUERY,
            filters: {
                "conversion_filter_suggests.filter_date_nyc": parsedDateFilter,
                "conversion_filter_suggests.advertiser_id": advertiserIdFilter,
                "conversion_meta_beacon.beacon_status": "READY",
                ...lookerMediaTypeFilter
            }
        };
        const hashKey = await this.sdk.looker.query.hashQuery(queryBody);
        return this.cache.promise(hashKey, () => this.sdk.looker.query
            .run(queryBody)
            .then(({ data, queryMeta }) => [
            {
                data: this.formatPixelResponse(data),
                queryMeta
            }
        ])
            .catch((e) => {
            console.error(e);
            throw new Error("Failed to fetch conversion dashboard pixel filter options");
        }));
    }
    /**
     * The same rows through the reporting API instead of the Looker SDK, in the
     * same shape, so no caller can tell which source answered.
     *
     * `beacon_status` is not sent as a filter here — unlike the Looker path,
     * which hard-codes `READY` itself, the endpoint already applies that
     * unconditionally whenever a pixel field is requested, so it is only listed
     * in `fields` to get it back on the row. `pixel_type` STATIC-inference is
     * likewise already applied server-side.
     */
    async findItemsFromReportingApi(filters) {
        const analysisInterval = requireAnalysisInterval(filters.where);
        const advertiserIdFilter = requireAdvertiserIdFilter(filters.where);
        const { reportTypes } = parseReportTypeFilter(filters.where);
        const request = {
            fields: ["beacon_id", "beacon_name", "pixel_type", "beacon_status"],
            filters: {
                analysis_interval: analysisInterval,
                advertiser_id: advertiserIdFilter,
                report_type: reportTypes.join(",")
            },
            sorts: ["beacon_name asc"],
            limit: -1
        };
        const hashKey = `api:${await this.sdk.looker.query.hashQuery(request)}`;
        return this.cache.promise(hashKey, () => this.sdk.looker.reporting
            .query("/attribution/cross-channel/filter-options", request, "Failed to fetch conversion dashboard pixel filter options.")
            .then(({ data, query }) => [
            {
                data: this.formatPixelApiResponse(data),
                queryMeta: { embedHash: toEmbedHash(query?.share_url) }
            }
        ]));
    }
    formatPixelApiResponse(rows) {
        const seenPixelIds = new Set();
        const pixels = [];
        rows.forEach((row) => {
            const pixelId = row.beacon_id;
            const pixelName = row.beacon_name;
            // if pixel type is missing we infer that it's STATIC
            const pixelType = row.pixel_type === "UNIVERSAL" ? "UNIVERSAL" : "STATIC";
            const pixelStatus = row.beacon_status;
            if (pixelId && !seenPixelIds.has(pixelId)) {
                pixels.push({
                    name: pixelName ?? "",
                    id: pixelId,
                    type: pixelType,
                    status: pixelStatus ?? ""
                });
                seenPixelIds.add(pixelId);
            }
        });
        return pixels;
    }
    formatPixelResponse(data) {
        const seenPixelIds = new Set();
        const pixels = [];
        data.forEach((pixel) => {
            const pixelId = pixel["conversion_filter_suggests.beacon_id"];
            const pixelName = pixel["conversion_meta_beacon.name"];
            // if pixel type is missing we infer that it's STATIC
            const pixelType = pixel["conversion_filter_suggests.pixel_type"] === "UNIVERSAL" ? "UNIVERSAL" : "STATIC";
            const pixelStatus = pixel["conversion_meta_beacon.beacon_status"];
            if (pixelId && !seenPixelIds.has(pixelId)) {
                pixels.push({
                    name: pixelName,
                    id: pixelId,
                    type: pixelType,
                    status: pixelStatus
                });
                seenPixelIds.add(pixelId);
            }
        });
        return pixels;
    }
    async saveItem() {
        throw new NotImplementedError("saveItem");
    }
    async deleteItem() {
        throw new NotImplementedError("deleteItem");
    }
}
//# sourceMappingURL=Pixel.js.map